import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { geoFrance } from '@afficheservices/db/schema'
import { sql } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema, FAQSchema } from '~/components/structured-data'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'
import { PROBLEMES } from '~/lib/problemes-data.server'
import { URGENCY_LABELS } from '~/lib/problemes-types'
import { SERVICE_LABELS } from '~/lib/programmatic-seo'
import {
  ArrowLeft, ArrowRight, CheckCircle, XCircle,
  Euro, Phone, MapPin,
} from 'lucide-react'

export function headers() { return SEO_PAGE_HEADERS }

export async function loader({ params }: LoaderFunctionArgs) {
  const problem = PROBLEMES.find((p) => p.slug === params.slug)
  if (!problem) throw new Response('Not Found', { status: 404 })

  // Prioritise the WHITELIST cities for THIS problem — the index-eligible pages
  // we most want Googlebot to reach. The hub previously linked only the top-30
  // by population, orphaning the ~500 whitelist city pages that actually rank
  // (GSC audit 2026-07: category hubs reached ~30 of 500+). Link the proven set
  // (citySlugs, guaranteed to resolve), padded with big cities if thin.
  const wl = (await db.execute(
    sql`SELECT split_part(path, '/', 4) AS slug FROM seo_index_whitelist WHERE path LIKE ${'/problemes/' + problem.slug + '/%'} ORDER BY path LIMIT 250`,
  )) as unknown as Array<{ slug: string }>
  const citySlugs = wl.map((r) => r.slug).filter(Boolean)
  if (citySlugs.length < 30) {
    const top = await db
      .select({ name: geoFrance.communeName })
      .from(geoFrance)
      .orderBy(sql`${geoFrance.population} DESC NULLS LAST`)
      .limit(30)
    const seen = new Set(citySlugs)
    for (const c of top) {
      const s = slugifyCity(c.name)
      if (!seen.has(s)) { citySlugs.push(s); seen.add(s) }
    }
  }
  return { problem, cities: citySlugs }
}

function deslug(s: string) {
  return s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return []
  const p = data.problem
  return [
    { title: `${p.title} — Que faire, coût, artisan | AfficheServices` },
    { name: 'description', content: `${p.shortDesc} Coût moyen : ${p.avgCost}. Trouvez un artisan disponible immédiatement.` },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/problemes/${p.slug}` },
    { property: 'og:title', content: `${p.title} — Que faire | AfficheServices` },
    { property: 'og:description', content: `${p.shortDesc} Coût : ${p.avgCost}.` },
    { property: 'og:url', content: `https://www.afficheservices.fr/problemes/${p.slug}` },
  ]
}

function slugifyCity(city: string) {
  return city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

export default function ProblemeDetailPage() {
  const { problem: p, cities } = useLoaderData<typeof loader>()
  const urg = URGENCY_LABELS[p.urgency]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Problèmes', url: 'https://www.afficheservices.fr/problemes' },
        { name: p.title, url: `https://www.afficheservices.fr/problemes/${p.slug}` },
      ]} />
      {p.faq.length > 0 && <FAQSchema items={p.faq.map((f) => ({ question: f.q, answer: f.a }))} />}

      <section className="bg-gradient-to-br from-red-900 via-rose-900 to-slate-900 px-4 py-12 text-white sm:py-16">
        <div className="mx-auto max-w-4xl">
          <Link to="/problemes" className="inline-flex items-center gap-1 text-xs font-semibold text-red-200 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Tous les problèmes
          </Link>
          <div className="mt-4 flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${urg.color}`}>{urg.label}</span>
          </div>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl">{p.title}</h1>
          <p className="mt-3 max-w-2xl text-base text-red-200">{p.shortDesc}</p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold text-white">
            <Euro className="h-4 w-4" /> Coût moyen : {p.avgCost}
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-5xl gap-8 px-4 py-12 lg:grid-cols-3 lg:px-6">
        <div className="space-y-10 lg:col-span-2">
          <section>
            <h2 className="text-xl font-bold text-slate-900">Comprendre le problème</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-700">{p.description}</p>
          </section>

          <section>
            <h2 className="flex items-center gap-2 text-xl font-bold text-emerald-800">
              <CheckCircle className="h-5 w-5" /> Que faire
            </h2>
            <ol className="mt-4 space-y-2">
              {p.whatToDo.map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </section>

          <section className="rounded-2xl border border-red-200 bg-red-50 p-6">
            <h2 className="flex items-center gap-2 text-xl font-bold text-red-800">
              <XCircle className="h-5 w-5" /> À ne pas faire
            </h2>
            <ul className="mt-4 space-y-2">
              {p.dontDo.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" /> {item}
                </li>
              ))}
            </ul>
          </section>

          {p.aiClaims && p.aiClaims.length > 0 && (
            <section className="rounded-2xl border border-blue-200 bg-blue-50 p-6">
              <h2 className="flex items-center gap-2 text-xl font-bold text-blue-900">
                📊 En chiffres
              </h2>
              <ul className="mt-4 space-y-2">
                {p.aiClaims.map((claim, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-blue-900">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
                    {claim}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {p.faq.length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-slate-900">Questions fréquentes</h2>
              <div className="mt-4 space-y-3">
                {p.faq.map((item, i) => (
                  <details key={i} className="group rounded-xl border border-border bg-white p-4">
                    <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900">
                      {item.q}
                      <ArrowRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* City internal links — programmatic SEO */}
          <section>
            <h2 className="text-lg font-bold text-slate-900">{p.title} par ville</h2>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {cities.map((citySlug) => (
                <Link
                  key={citySlug}
                  to={`/problemes/${p.slug}/${citySlug}`}
                  className="rounded-full border border-border px-2.5 py-1 text-[11px] text-slate-600 hover:border-primary hover:text-primary"
                >
                  <MapPin className="mr-0.5 inline h-3 w-3" /> {p.title} {deslug(citySlug)}
                </Link>
              ))}
            </div>
          </section>
        </div>

        <aside>
          <div className="sticky top-20 space-y-4">
            <div className="rounded-2xl border border-border bg-white p-6 shadow-md">
              <h3 className="text-sm font-bold text-slate-900">Trouver un artisan</h3>
              <p className="mt-1 text-xs text-slate-500">Recevez 3 devis gratuits en 48h</p>
              <Link
                to="/devis-gratuit"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary-hover"
              >
                Devis gratuit <ArrowRight className="h-4 w-4" />
              </Link>
              {p.urgency === 'urgent' && (
                <Link
                  to="/urgence"
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 ring-1 ring-red-200 hover:bg-red-100"
                >
                  <Phone className="h-4 w-4" /> Urgences 24h
                </Link>
              )}
            </div>

            <div className="rounded-2xl border border-border bg-slate-50 p-5">
              <p className="text-xs font-semibold text-slate-600">Services concernés</p>
              <ul className="mt-3 space-y-2">
                {p.serviceSlugs.map((slug) => {
                  const svc = SERVICE_LABELS[slug]
                  return svc ? (
                    <li key={slug}>
                      <Link to={`/services/${slug}`} className="flex items-center justify-between text-sm text-slate-700 hover:text-primary">
                        {svc.name} <ArrowRight className="h-3 w-3" />
                      </Link>
                    </li>
                  ) : null
                })}
              </ul>
            </div>
          </div>
        </aside>
      </div>

      <Footer />
    </div>
  )
}
